import React, { useEffect, useRef, useState } from 'react';
import useLogbookInbox from '../Hooks/useLogbookInbox';
import { isHttpHref, PHASE_LABELS } from '../Utils/logbookInbox';
import './LogbookInboxBell.css';

function NotificationGlyph() {
  return (
    <svg
      className="logbook-inbox-glyph"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6.4 9.6a5.6 5.6 0 0 1 11.2 0c0 4.2 1.4 5.5 1.4 5.5H5s1.4-1.3 1.4-5.5" />
      <path d="M10 18.2a2 2 0 0 0 4 0" />
    </svg>
  );
}

function InboxRow({ item, quiet, onDismiss }) {
  const href = isHttpHref(item.href) ? item.href : null;
  const chipLabel = PHASE_LABELS[item.phase] || item.phase;
  const chipTone = item.severity === 'due' ? 'due' : 'warning';

  const openHref = () => {
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`logbook-inbox-row${quiet ? ' is-quiet' : ''}${href ? ' is-open-link' : ''}`}
      onClick={href ? openHref : undefined}
      role={href ? 'link' : undefined}
    >
      <div className="logbook-inbox-copy">
        <div className="logbook-inbox-title-row">
          <div className="logbook-inbox-title">{item.title || 'Alert'}</div>
          {chipLabel && (
            <span className={`logbook-inbox-chip ${chipTone}`}>{chipLabel}</span>
          )}
        </div>
        {item.body ? <div className="logbook-inbox-body">{item.body}</div> : null}
      </div>
      {!quiet && (
        <button
          type="button"
          className="logbook-inbox-dismiss"
          onClick={(event) => {
            event.stopPropagation();
            onDismiss(item.sourceKey, item.phase);
          }}
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

export default function LogbookInboxBell() {
  const { unread, seen, badgeCount, dismiss } = useLogbookInbox();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const hasRows = unread.length > 0 || seen.length > 0;

  useEffect(() => {
    const handleClick = (event) => {
      if (!open) return;
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (open && event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    const api = typeof window !== 'undefined' ? window.electronAPI : null;
    if (!api?.onOpenLogbookInbox) return undefined;
    const unsub = api.onOpenLogbookInbox(() => {
      setOpen(true);
    });
    return () => {
      try { unsub?.(); } catch (_) {}
    };
  }, []);

  return (
    <div ref={containerRef} className="logbook-inbox clickable" data-clickable="true">
      <button
        type="button"
        className={`logbook-inbox-trigger${open ? ' is-open' : ''}`}
        aria-label={badgeCount ? `${badgeCount} logbook alerts` : 'Logbook alerts'}
        title={badgeCount ? `${badgeCount} logbook alerts` : 'Logbook alerts'}
        onClick={() => setOpen((value) => !value)}
      >
        <NotificationGlyph />
        {badgeCount > 0 && (
          <span className="logbook-inbox-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
        )}
      </button>
      {open && (
        <div className="logbook-inbox-panel">
          <div className="logbook-inbox-header">Logbook</div>
          {!hasRows && <div className="logbook-inbox-empty">No alerts</div>}
          {unread.length > 0 && (
            <div className="logbook-inbox-section">
              {unread.map((item) => (
                <InboxRow key={item.sourceKey} item={item} onDismiss={dismiss} />
              ))}
            </div>
          )}
          {seen.length > 0 && (
            <div className="logbook-inbox-section">
              <div className="logbook-inbox-section-label">Seen</div>
              {seen.map((item) => (
                <InboxRow key={item.sourceKey} item={item} quiet onDismiss={dismiss} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
